/**
 * REVIVE OUTFIT - Low Stock Indicator Custom Element
 * Dynamically binds to variant changes and updates stock count & progress bar
 */

class ReviveLowStock extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 5;
    this.showQuantity = this.dataset.showQuantity === 'true';
    this.showProgress = this.dataset.showProgress === 'true';
    this.showOnlyTracked = this.dataset.showOnlyTracked === 'true';
    this.isPreview = false;
    try {
      this.stockByVariant = JSON.parse(this.querySelector('[data-rls-stock-map]')?.textContent || '{}');
    } catch (_) {
      this.stockByVariant = {};
    }
    this.headingTemplate = this.dataset.headingTemplate || 'Hurry! Limited stock remaining';
    this.neutralTemplate = this.dataset.neutralTemplate || 'Discover your next favorite style';

    this.headingEl = this.querySelector('[data-rls-heading]');
    this.countEl = this.querySelector('[data-rls-count]');
    this.badgeEl = this.querySelector('[data-rls-quantity-badge]');
    this.progressFillEl = this.querySelector('[data-rls-progress-fill]');
    this.progressBarEl = this.querySelector('[role="progressbar"]');

    const initialStock = this.stockByVariant[this.dataset.initialVariantId];
    if (initialStock) this.updateState(initialStock);

    // Listen to Horizon & Shopify events
    window.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('change', this.onVariantChange);

    const section = this.closest('.shopify-section, [data-section-id]');
    if (section) {
      section.addEventListener('shopify:productSelect', this.onVariantChange);
      section.addEventListener('product:variant-change', this.onVariantChange);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('change', this.onVariantChange);
  }

  handleVariantChange(event) {
    let variant = null;

    if (event.detail && event.detail.variant) {
      variant = event.detail.variant;
    } else if (event.detail && event.detail.newVariant) {
      variant = event.detail.newVariant;
    } else if (event.target && event.target.matches && event.target.matches('[name="id"], [name^="options["]')) {
      // In preview or empty catalog mode, simulate variant stock variation
      if (this.isPreview) {
        const val = event.target.value ? event.target.value.toLowerCase() : '';
        if (val.includes('s') || val.includes('small')) {
          this.updateState({ managed: true, qty: 2 });
        } else if (val.includes('m') || val.includes('medium')) {
          this.updateState({ managed: true, qty: 4 });
        } else if (val.includes('l') || val.includes('large')) {
          this.updateState({ managed: true, qty: 9 }); // Plentiful -> hides
        } else if (val.includes('xl')) {
          this.updateState({ managed: true, qty: 0 }); // Sold out
        }
        return;
      }
    }

    const id = variant?.id ?? event.detail?.variantId ?? event.detail?.resource?.id;
    if (id == null) return;
    const normalizedId = String(id).split('/').pop();
    const stock = this.stockByVariant[normalizedId];
    if (stock) {
      this.updateState(stock);
    } else {
      this.classList.add('revive-low-stock--hidden');
    }
  }

  updateState({ managed, qty }) {
    if (this.isPreview) {
      // Keep visible in preview mode
      this.classList.remove('revive-low-stock--hidden');
      if (qty !== undefined && qty > 0 && qty <= this.threshold) {
        if (this.countEl) this.countEl.textContent = qty;
        if (this.badgeEl) this.badgeEl.style.display = '';
        if (this.headingEl) this.headingEl.textContent = this.headingTemplate;
        this.updateProgressBar(qty);
      } else if (qty > this.threshold) {
        this.classList.add('revive-low-stock--hidden');
      }
      return;
    }

    if (!managed) {
      if (this.showOnlyTracked) {
        this.classList.add('revive-low-stock--hidden');
      } else {
        // Show neutral message without fake number
        this.classList.remove('revive-low-stock--hidden');
        if (this.headingEl) this.headingEl.textContent = this.neutralTemplate;
        if (this.badgeEl) this.badgeEl.style.display = 'none';
        if (this.progressBarEl) this.progressBarEl.style.display = 'none';
      }
      return;
    }

    if (qty > 0 && qty <= this.threshold) {
      this.classList.remove('revive-low-stock--hidden');
      if (this.headingEl) this.headingEl.textContent = this.headingTemplate;
      if (this.countEl) this.countEl.textContent = qty;
      if (this.badgeEl) this.badgeEl.style.display = this.showQuantity ? '' : 'none';
      if (this.progressBarEl) this.progressBarEl.style.display = this.showProgress ? '' : 'none';
      this.updateProgressBar(qty);
    } else {
      // Inventory is above threshold or zero
      this.classList.add('revive-low-stock--hidden');
    }
  }

  updateProgressBar(qty) {
    if (!this.progressFillEl || !this.progressBarEl) return;
    let pct = Math.round((qty / this.threshold) * 100);
    pct = Math.max(12, Math.min(100, pct));
    this.progressFillEl.style.width = pct + '%';
    this.progressBarEl.setAttribute('aria-valuenow', pct);
  }
}

if (!customElements.get('revive-low-stock')) {
  customElements.define('revive-low-stock', ReviveLowStock);
}
